/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {Vue} from 'vue-property-decorator';
import Router, {Route} from 'vue-router';
import Home from '@/views/Home/index.vue';
import Authenticate from '@/views/Authenticate/index.vue';
import {authenticated} from '@/router/authentication-guard';
import {notFoundGuard} from '@/router/not-found-guard';
import {focusHook} from '@/router/focus-hook';
import {oAuthGuard} from '@/router/oauth-guard';
import MyIssues from '@/views/MyIssues/index.vue';
// import ReleasedFixes from '@/views/ReleasedFixes/index.vue';
import Issues from '@/views/Issues/index.vue';
import IssueDetail from '@/views/IssueDetails/index.vue';
import ReportIssue from '@/views/ReportIssue/index.vue';
import LoginRequired from '@/views/LoginRequired/index.vue';
import Logout from '@/views/Logout/index.vue';
import {configurationGuard} from '@/router/configuration-guard';
import EditIssue from '@/views/EditIssue/index.vue';
import Faq from '@/views/Faq/index.vue';
import {eliteGuard} from '@/router/elite-guard';
import ReportedIssues from '@/views/ReportedIssues/index.vue';
import ReportedIssuesGameSelect from '@/views/ReportedIssuesGameSelect/index.vue';
import ReportedIssuesGame from '@/views/ReportedIssuesGame/index.vue';

Vue.use(Router);

/**
 * Router object exposed for view navigation.
 *
 * This example router contains an example of static, nested and component based rendering.
 * e.g. /about (static)
 * e.g. /home (component)
 * e.g. /nested/terms (nested)
 *
 * Example route guards:
 * 1. authenticated - guard to check vuex session module for non-expired JWT token.
 * 2. steamAuthenticateReady - guard to check that steam authentication route contains properly configured params.
 * 3. notFoundGuard - route not found guard (typically used to redirect to a 404 etc).
 * 4. focusHook - resets the focus on a route change.
 *
 * Example: adding Steam authentication routes with steam guard
 * {
 *      path: '/steam/authenticate',
 *      name: 'authenticate',
 *      beforeEnter: steamAuthenticateReady,
 *      props: (route: Route) => ({ code: route.query.code }),
 *      pathToRegexpOptions: { strict: true },
 *  }
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
const router: Router = new Router({
        mode: 'history',
        base: process.env.BASE_URL,
        // tslint:disable-next-line
        scrollBehavior: (to, from, savedPosition) => {
            return { x: 0, y: 0 };

            // * Test for scrolling for search in home -
            // * does't refresh for same page but maybe use it
            // return new Promise((resolve, reject) => {
            //     setTimeout(() => {
            //         if (to.hash) {
            //             resolve({selector: to.hash});
            //         }
            //     }, 500);
            // });
        },
        routes: [
            /* gauss redirect */
            {
                path: '/reported-issues/chaos-gate',
                redirect: '/reported-issues/chaos-gate-daemonhunters',
            },
            {
                path: '/',
                name: 'home',
                component: Home,
            },
            {
                path: '/faqs',
                name: 'faqs',
                component: Faq,
            },
            {
                path: '/my-issues',
                name: 'my-issues',
                beforeEnter: authenticated,
                component: MyIssues,
            },
            {
                path: '/reported-issues',
                name: 'reported-issues',
                component: ReportedIssues,
                children: [
                    {
                        path: '/',
                        name: 'reported-issues',
                        component: ReportedIssuesGameSelect,
                    },
                    {
                        path: ':gameSlug',
                        name: 'reported-issues-game-slug',
                        component: ReportedIssuesGame,
                    },
                ],
            },
            // {
            //     path: '/released-fixes',
            //     name: 'released-fixes',
            //     component: ReleasedFixes,
            // },
            {
                path: '/report-issue',
                name: 'report-issue',
                beforeEnter: authenticated,
                component: ReportIssue,
            },
            {
                path: '/issues',
                name: 'issues',
                component: Issues,
            },
            {
                path: '/issue-detail/:id',
                name: 'issue-detail',
                component: IssueDetail,
            },
            {
                path: '/login-required',
                name: 'login-required',
                component: LoginRequired,
            },
            {
                path: '/authenticate',
                name: 'authenticate',
                component: Authenticate,
                beforeEnter: oAuthGuard,
                // tslint:disable-next-line
                props: (route: Route) => ({ code: route.query.code }),
                pathToRegexpOptions: { strict: true },
            },
            {
                path: '/logout',
                name: 'logout',
                component: Logout,
            },
            {
                path: '/edit-issue/:id',
                name: 'edit-issue',
                beforeEnter: authenticated,
                component: EditIssue,
            },
        ],
    });

router.beforeEach(eliteGuard);
router.beforeEach(notFoundGuard);
router.beforeEach(configurationGuard);
router.afterEach(focusHook);

export default router;
